/**
 * Справочник тарифов (docs/billing/plan.md §2).
 *
 * Живёт в коде, а не в БД: тарифная сетка меняется редко и осознанно, деплой —
 * приемлемая цена за версионирование через git. Точечные отступления под конкретный
 * договор — `workspaces.plan_limits` (jsonb), см. {@link resolveEntitlements}.
 *
 * Один источник правды для сервера (enforcement) и веба (paywall-UI): расхождение
 * каталога между ними означало бы «кнопка разрешает, сервер отказывает».
 */

export type PlanId = 'free' | 'pro' | 'pro_max' | 'enterprise';

/**
 * Возможности, привязанные к тарифу.
 *
 * Первая группа гейтится в v1 (носитель существует). Вторая — «скоро»: место в
 * каталоге застолблено по прайсу, но в коде эти флаги пока никто не проверяет —
 * носители фич появятся отдельными ветками (plan.md §1).
 */
export const BILLING_FEATURES = [
  'subdomain',
  'integrations',
  'custom_fonts',
  'sheet_editor',
  'tile_formats',
  'branding_optional',
  'format_geopackage',
  'format_parquet',
  'noindex_toggle',
  'map_password',
  'analytics',
] as const;

export type BillingFeature = (typeof BILLING_FEATURES)[number];

export interface PlanLimits {
  /** null — без лимита. */
  projects: number | null;
  storageBytes: number | null;
  viewsPerMonth: number | null;
}

/** Ступень цены места PRO MAX: `upTo` — включительно, null — «и дальше». */
export interface PerSeatTier {
  upTo: number | null;
  monthlyRub: number;
}

export type PlanPricing =
  | { kind: 'free' }
  | { kind: 'flat'; monthlyRub: number }
  | { kind: 'per_seat'; tiers: readonly PerSeatTier[] }
  | { kind: 'custom' };

export interface PlanDef {
  id: PlanId;
  /** Название для UI («Старт», «PRO», …). */
  title: string;
  pricing: PlanPricing;
  limits: PlanLimits;
  features: readonly BillingFeature[];
}

const MB = 1024 * 1024;
const GB = 1024 * MB;

const PRO_FEATURES: readonly BillingFeature[] = [
  'custom_fonts',
  'sheet_editor',
  'tile_formats',
  'branding_optional',
  'noindex_toggle',
  'format_geopackage',
  'format_parquet',
];

const PRO_MAX_FEATURES: readonly BillingFeature[] = [
  ...PRO_FEATURES,
  'subdomain',
  'integrations',
  'map_password',
  'analytics',
];

export const PLANS: Record<PlanId, PlanDef> = {
  free: {
    id: 'free',
    title: 'Старт',
    pricing: { kind: 'free' },
    limits: { projects: 3, storageBytes: 250 * MB, viewsPerMonth: 10_000 },
    features: [],
  },
  pro: {
    id: 'pro',
    title: 'PRO',
    // За место, как и PRO MAX (решение владельца 2026-08-16): флэт позволял одному
    // оплаченному воркспейсу обслуживать сколько угодно редакторов. Ступень одна —
    // объёмная скидка остаётся отличием старшего тарифа, здесь цена места постоянна.
    pricing: { kind: 'per_seat', tiers: [{ upTo: null, monthlyRub: 1490 }] },
    limits: { projects: null, storageBytes: 10 * GB, viewsPerMonth: 50_000 },
    features: PRO_FEATURES,
  },
  pro_max: {
    id: 'pro_max',
    title: 'PRO MAX',
    pricing: {
      kind: 'per_seat',
      tiers: [
        { upTo: 5, monthlyRub: 2990 },
        { upTo: 20, monthlyRub: 2790 },
        { upTo: 50, monthlyRub: 2690 },
        // 51+ мест — индивидуальные условия; самообслуживание закрыто
        // (SELF_SERVE_MAX_SEATS), цена нужна только для отображения «от».
        { upTo: null, monthlyRub: 2690 },
      ],
    },
    limits: { projects: null, storageBytes: 50 * GB, viewsPerMonth: 250_000 },
    features: PRO_MAX_FEATURES,
  },
  enterprise: {
    id: 'enterprise',
    title: 'Компания',
    pricing: { kind: 'custom' },
    // База — без лимитов: настоящие рамки конкретного договора задаются через
    // `plan_limits` руками из админки.
    limits: { projects: null, storageBytes: null, viewsPerMonth: null },
    features: PRO_MAX_FEATURES,
  },
};

/**
 * Больше — только через переговоры («Компания»), см. прайс «51+ — индивидуально».
 * Общий потолок для всех посеточных тарифов: цены сверх него нет ни у PRO, ни у PRO MAX.
 */
export const SELF_SERVE_MAX_SEATS = 50;

/** Тариф считается по местам? (free и «Компания» — нет.) */
export function isPerSeatPlan(planId: PlanId): boolean {
  return PLANS[planId].pricing.kind === 'per_seat';
}

/**
 * Сырой `workspaces.plan` → известный план. Неизвестное значение читается как free:
 * опечатка в БД не должна молча раздавать возможности. Legacy-план `business`
 * (заглушка времён workspaces) трактуем как pro_max (plan.md §2).
 */
export function resolvePlanId(raw: string | null | undefined): PlanId {
  const plan = (raw ?? '').trim();
  if (plan === 'business') return 'pro_max';
  if (plan === 'free' || plan === 'pro' || plan === 'pro_max' || plan === 'enterprise') {
    return plan;
  }
  return 'free';
}

/**
 * Цена одного места за месяц при данном числе платных мест. У PRO ступень одна,
 * у PRO MAX место дешевеет с ростом команды. Непосеточный тариф — `null`.
 */
export function seatPriceRub(planId: PlanId, seats: number): number | null {
  const pricing = PLANS[planId].pricing;
  if (pricing.kind !== 'per_seat') {
    return null;
  }
  for (const tier of pricing.tiers) {
    if (tier.upTo === null || seats <= tier.upTo) {
      return tier.monthlyRub;
    }
  }
  // Недостижимо, пока последний tier открыт (upTo: null); страховка от правки каталога.
  return pricing.tiers[pricing.tiers.length - 1]!.monthlyRub;
}

/**
 * Месячная цена плана в рублях. `null` — план не покупается самообслуживанием
 * (free, enterprise, посеточный тариф сверх потолка мест).
 */
export function planMonthlyPriceRub(planId: PlanId, billableSeats: number): number | null {
  const pricing = PLANS[planId].pricing;
  switch (pricing.kind) {
    case 'flat':
      return pricing.monthlyRub;
    case 'per_seat': {
      if (billableSeats > SELF_SERVE_MAX_SEATS) return null;
      // Воркспейс без платных участников невозможен (owner всегда billable),
      // но защищаемся от нуля, чтобы не выставить счёт на 0 ₽.
      const seats = Math.max(1, billableSeats);
      const price = seatPriceRub(planId, seats);
      return price === null ? null : seats * price;
    }
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Пробный период                                                     */
/* ------------------------------------------------------------------ */

/**
 * Бесплатные дни при первом переходе с бесплатного «Старта» на платный тариф
 * (решение владельца 2026-08-16): карта указывается сразу, первое списание — после.
 */
export const TRIAL_DAYS = 14;

/**
 * Сумма проверочной блокировки карты при старте пробного периода.
 *
 * Платёжного токена без транзакции CloudPayments не выдаёт, а подписка
 * (`subscriptions/create`) без токена невозможна — поэтому карта проверяется
 * двухстадийным платежом на минимальную сумму, которую мы тут же отменяем
 * (деньги разблокируются мгновенно, чек не пробивается — платежа не было).
 */
export const TRIAL_CARD_HOLD_RUB = 1;

/** Конец пробного периода от момента подключения. */
export function trialEndsAt(from: Date | number = Date.now()): Date {
  const start = typeof from === 'number' ? from : from.getTime();
  return new Date(start + TRIAL_DAYS * 86_400_000);
}

/* ------------------------------------------------------------------ */
/*  Истечение оплаченного периода                                      */
/* ------------------------------------------------------------------ */

/** Сбой продления: сколько дней план живёт после конца оплаченного периода (plan.md §4). */
export const BILLING_GRACE_DAYS = 3;

/** Состояние оплаты в объёме, нужном для ответа «период ещё действует?». */
export interface BillingPeriodState {
  source?: string | null;
  status?: string | null;
  currentPeriodEnd?: Date | string | null;
  paidUntil?: Date | string | null;
}

function asTime(value: Date | string | null | undefined): number | null {
  if (!value) return null;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

/**
 * Оплаченный период закончился?
 *
 * Смотрим на ДАТУ, а не на статус подписки: строка может остаться `active` навсегда —
 * например, если подписка в CloudPayments так и не создалась и продлевать нечего.
 * Именно дата — единственное, что нельзя подделать со стороны клиента (plan.md §4).
 *
 * `admin_test` и строки без данных о периоде не истекают: первое выключается руками,
 * второе — план, выданный мимо биллинга, и понижать его нам нечем.
 */
export function isBillingPeriodExpired(
  billing: BillingPeriodState | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!billing) return false;
  // Grace нужен ровно затем, чтобы пережить ретраи неудавшегося списания. У отменённой
  // подписки ретраев не будет: продлевать нечем, и лишние три дня — просто подарок
  // сверх оплаченного периода. На пробном периоде это заметнее всего: отменивший до
  // первого списания получал бы 17 дней там, где интерфейс обещает 14.
  const graceMs = billing.status === 'cancelled' ? 0 : BILLING_GRACE_DAYS * 86_400_000;
  if (billing.source === 'cloudpayments') {
    const end = asTime(billing.currentPeriodEnd);
    return end !== null && end + graceMs < now;
  }
  if (billing.source === 'manual') {
    const until = asTime(billing.paidUntil);
    return until !== null && until < now;
  }
  return false;
}

/**
 * План с учётом истечения периода: просроченная оплата читается как `free` ещё до того,
 * как ночной джоб перепишет `workspaces.plan` (plan.md §4 — «ловим лениво при чтении»).
 */
export function effectivePlanId(
  plan: string | null | undefined,
  billing: BillingPeriodState | null | undefined,
  now?: number,
): PlanId {
  const planId = resolvePlanId(plan);
  if (planId === 'free') return planId;
  return isBillingPeriodExpired(billing, now) ? 'free' : planId;
}

/** Возможности и лимиты воркспейса после применения `plan_limits`. Сериализуемо. */
export interface WorkspaceEntitlements {
  planId: PlanId;
  features: BillingFeature[];
  limits: PlanLimits;
}

/** Выводится из каталога: новая фича в `BILLING_FEATURES` сразу проходит валидацию override'ов. */
const KNOWN_FEATURES: ReadonlySet<string> = new Set<string>(BILLING_FEATURES);

/** `plan_limits` приходит из jsonb — форму проверяем, а не утверждаем через `as`. */
function readOverrides(planLimits: unknown): {
  features: BillingFeature[];
  limits: Partial<PlanLimits>;
} {
  if (!planLimits || typeof planLimits !== 'object' || Array.isArray(planLimits)) {
    return { features: [], limits: {} };
  }
  const raw = planLimits as Record<string, unknown>;

  const features: BillingFeature[] = [];
  if (Array.isArray(raw.features)) {
    for (const item of raw.features) {
      if (typeof item === 'string' && KNOWN_FEATURES.has(item.trim())) {
        features.push(item.trim() as BillingFeature);
      }
    }
  }

  const limits: Partial<PlanLimits> = {};
  const rawLimits =
    raw.limits && typeof raw.limits === 'object' && !Array.isArray(raw.limits)
      ? (raw.limits as Record<string, unknown>)
      : {};
  for (const key of ['projects', 'storageBytes', 'viewsPerMonth'] as const) {
    const value = rawLimits[key];
    if (value === null) {
      limits[key] = null;
    } else if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      limits[key] = value;
    }
  }
  return { features, limits };
}

/**
 * План + `plan_limits` → итоговые возможности и лимиты.
 *
 * Переопределения только расширяют: фичи — union, лимиты — берём больший (или null
 * как «без лимита»). Отзыв возможностей = смена плана, не override (plan.md §2,
 * правило из workspaces: «только выдача, не отзыв»).
 */
export function resolveEntitlements(
  plan: string | null | undefined,
  planLimits: unknown,
  /** Состояние оплаты: просроченный период понижает план до free ещё до ночного джоба. */
  billing?: BillingPeriodState | null,
): WorkspaceEntitlements {
  const planId = effectivePlanId(plan, billing);
  const def = PLANS[planId];
  const overrides = readOverrides(planLimits);

  const features = new Set<BillingFeature>(def.features);
  for (const feature of overrides.features) {
    features.add(feature);
  }

  const limits: PlanLimits = { ...def.limits };
  for (const key of ['projects', 'storageBytes', 'viewsPerMonth'] as const) {
    if (!(key in overrides.limits)) continue;
    const override = overrides.limits[key];
    const base = limits[key];
    if (override === null) {
      limits[key] = null;
    } else if (override !== undefined && base !== null && override > base) {
      limits[key] = override;
    }
  }

  return { planId, features: [...features], limits };
}

/**
 * Минимальный платный план, дающий возможность, — для paywall-сообщений
 * («нужен PRO» / «нужен PRO MAX»).
 */
export function requiredPlanFor(feature: BillingFeature): PlanId {
  return PLANS.pro.features.includes(feature) ? 'pro' : 'pro_max';
}

/**
 * Повод показать paywall: фича тарифа или упор в числовой лимит.
 * Ключ общий для серверного 403 `{code: 'plan_limit', feature}` и текстов модалки —
 * сервер и веб обязаны говорить на одном словаре.
 * `views` здесь нет: лимит просмотров мягкий и ничего не блокирует (plan.md §6).
 */
export type PaywallReason = BillingFeature | 'projects' | 'storage' | 'seats';

/** Следующий тариф вверх; null — выше только «Компания» (переговоры). */
export function nextPlanUp(plan: PlanId): PlanId | null {
  if (plan === 'free') return 'pro';
  if (plan === 'pro') return 'pro_max';
  return null;
}

/** Какой план предлагать в paywall'е: для лимитов — следующий вверх от текущего. */
export function requiredPlanForReason(reason: PaywallReason, currentPlan: PlanId): PlanId {
  // Упор в потолок мест — единственный повод, где вверх ведёт не кнопка, а разговор:
  // сверх SELF_SERVE_MAX_SEATS цены в прайсе нет («51+ — индивидуально»).
  if (reason === 'seats') {
    return 'enterprise';
  }
  if (reason === 'projects' || reason === 'storage') {
    return nextPlanUp(currentPlan) ?? 'pro_max';
  }
  return requiredPlanFor(reason);
}
